// components/VMRS/ExploreTasking/components/form/UnifiedDynamicForm.tsx
import { Input, Select, DatePicker, InputNumber, Radio, Slider } from "antd";
import dayjs from "dayjs";
import RangeInput from "../../../ExploreArchive/RangeInput";
import { useTranslation } from "react-i18next";
import CommonIcons from "../../../../../assets/icons";

interface UnifiedDynamicFormProps
{
   step: any;           // contains schema, ui (type field ignored)
   formData: Record<string, any>;
   setFormData: (data: any) => void;
   type?: string; // edit || add
   display?: string; // grid || col
   disablePastDates?: boolean; // default true for tasking (future only). Set false for archive (past only).
}

/**
 * Unified dynamic form renderer that handles all widget types without relying on step.type.
 * Renders fields based on ui.order and ui.options[key].widget.
 *
 * Special handling:
 * - Two consecutive "range" widgets with matching min/max pattern are combined into one RangeInput
 * - The second field is skipped (rendered as part of the pair)
 * - Widgets "rangeInput" and "range_minmax" render a RangeInput that stores value as [min, max] array
 */
const UnifiedDynamicForm = ({
   step,
   formData,
   setFormData,
   type,
   display,
   disablePastDates = true
}: UnifiedDynamicFormProps) =>
{
   const { schema, ui } = step;
   const { t } = useTranslation();

   if (!schema)
   {
      return <div className="p-4 text-gray-400">Fetching schema...</div>;
   }

   const isEditable = (config: any) =>
   {
      if (type !== "edit") return true; // add mode => edit all
      return config?.can_edit !== false; // chỉ disable khi canEdit = false
   };

   // Helper to check if two fields form a min/max pair
   const isMinMaxPair = (key1: string, key2: string, config1: any, config2: any) =>
   {
      // Both must be range widgets
      if (config1.widget !== "range" || config2.widget !== "range")
      {
         return false;
      }

      // Check if they form a min/max pair by name pattern
      const key1Lower = key1.toLowerCase();
      const key2Lower = key2.toLowerCase();

      // patterns: min_... / max_..., min... / max..., lower/upper, etc.
      const isMin = key1Lower.includes("min") || key1Lower.startsWith("lower");
      const isMax = key2Lower.includes("max") || key2Lower.startsWith("upper");

      // Also check if they share the same base name (e.g., min_off_nadir and max_off_nadir)
      const base1 = key1Lower.replace(/^(min|lower)_?/, "").replace(/_/g, "");
      const base2 = key2Lower.replace(/^(max|upper)_?/, "").replace(/_/g, "");

      return (isMin && isMax) || base1 === base2;
   };

   // Check if a field should be rendered based on dependency conditions
   // Supports both new format (depends_on: {field, value}) and legacy format (depField: "fieldName")
   // const shouldRenderField = (key: string, config: any) =>
   // {
   //       if (!config.depends_on && !config.depField) return true;

   //       let depField: string;
   //       let depValue: string;

   //       if (config.depends_on)
   //       {
   //             depField = config.depends_on.field;
   //             depValue = config.depends_on.value;
   //       }
   //       else if (config.depField)
   //       {
   //             depField = config.depField;
   //             depValue = key; // Legacy: field is shown when controller equals this field's key
   //       }

   //       // Get the controller field's value from formData, or its default if not set
   //       const controllerConfig = ui?.options?.[depField];
   //       const controllerDefault = controllerConfig?.default;
   //       const controllerValue = formData[depField] ?? controllerDefault;

   //       return controllerValue === depValue;
   // };

   const shouldRenderField = (key: string, config: any) =>
   {
      if (!config.depends_on && !config.depField) return true;

      const depField = config.depends_on?.field ?? config.depField;
      const depValue = config.depends_on?.value ?? key;

      if (!depField) return true;

      const controllerValue =
         formData[depField] ?? ui?.options?.[depField]?.default;

      return controllerValue === depValue;
   };

   const renderField = (key: string, config: any) =>
   {
      const value = formData[key];

      switch (config.widget)
      {
         case "input":
            return (
               <Input
                  className="w-full div-dropdown"
                  key={key}
                  placeholder={t(config.placeholder)}
                  value={value ?? ""}
                  onChange={(e) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: e.target.value,
                     }))
                  }
                  disabled={!isEditable(config)}
                  maxLength={config?.maxLength ?? 100}
               />
            );

         case "textarea":
            return (
               <Input.TextArea
                  className="w-full div-dropdown"
                  key={key}
                  placeholder={t(config.placeholder)}
                  value={value ?? ""}
                  onChange={(e) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: e.target.value,
                     }))
                  }
                  disabled={!isEditable(config)}
                  maxLength={config?.maxLength ?? 256}
               />
            );

         case "select":


            return (
               <Select
                  className="w-full div-dropdown"
                  key={key}


                  options={config.options?.map((o: any) => ({
                     label: o.label || o.label || o.value,
                     value: o.value,
                  }))?.concat(config.mode === "multiple"
                     ? [{ label: "All", value: "all" }] : [])}
                  placeholder={t(config.placeholder)}
                  value={value}
                  // onChange={(val) =>
                  //    setFormData((prev: any) => ({
                  //       ...prev,
                  //       [key]: val,
                  //    }))
                  // }
                  onChange={(val) =>
                  {
                     if (config.mode === "multiple")
                     {
                        if (val.includes("all"))
                        {
                           const allValues = config.options?.map((o: any) => o.value);

                           setFormData((prev: any) => ({
                              ...prev,
                              [key]: allValues,
                           }));

                           return;
                        }
                     }

                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: val,
                     }));
                  }}
                  disabled={!isEditable(config)}
                  mode={config.mode}
               />
            );

         case "date":
         case "date-picker":
            const dateValue = value ? dayjs(value) : null;
            const isDateRange = key === "date_from" || key === "date_to";
            let disabledDate: ((current: dayjs.Dayjs) => boolean) | undefined = undefined;

            if (isDateRange)
            {
               if (disablePastDates)
               {
                  // For tasking: disable past dates (future only)
                  disabledDate = (current: dayjs.Dayjs) => current && current < dayjs().startOf('day');
               }
               else
               {
                  // For archive: disable future dates (past only)
                  disabledDate = (current: dayjs.Dayjs) => current && current > dayjs().endOf('day');
               }
            }

            return (
               <DatePicker
                  format="DD/MM/YYYY"
                  key={key}
                  className="w-full div-dropdown"
                  value={dateValue}
                  disabledDate={disabledDate}
                  onChange={(date) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: date ? date.toISOString() : null,
                     }))
                  }
                  disabled={!isEditable(config)}
                  placeholder={t(config.placeholder)}
               />
            );

         case "number":
            return (
               <InputNumber
                  key={key}
                  className="w-full div-dropdown"
                  value={value}
                  onChange={(val) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: val,
                     }))
                  }
                  disabled={!isEditable(config)}
                  placeholder={t(config.placeholder)}
               />
            );

         case "slider":
            return (
               <Slider
                  className="w-full"
                  key={key}
                  min={config.min ?? 0}
                  max={config.max ?? 100}
                  step={config.step ?? 1}
                  value={value ?? config.default ?? (config.min ?? 0)}
                  onChange={(val) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: val,
                     }))
                  }
                  disabled={!isEditable(config)}
               />
            );

         case "radio":
            return (
               <Radio.Group
                  key={key}
                  options={config.options?.map((o: any) => ({
                     label: t(o.label) || t(o.label) || t(o.value),
                     value: o.value,
                  }))}
                  value={value}
                  onChange={(e) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [key]: e.target.value,
                     }))
                  }
                  disabled={!isEditable(config)}
               />
            );

         case "range":
            // This is a standalone range field (not part of a pair)
            // For consistency, we still use RangeInput but with a single value
            // However, the RangeInput component expects a [min, max] tuple
            // So we'll render a placeholder if not paired
            return (
               <div key={key} className="text-red-400">
                  Range widget must be paired with another range field (min/max pair). Found unpaired range: {key}
               </div>
            );

         case "rangeInput":
         case "range_minmax":
            // RangeInput-like widgets: store value as [min, max] array in a single field
            const rmKey = key;
            const rmConfig = config;
            const rawValue = formData[rmKey];
            let rangeArray: [number, number];
            if (Array.isArray(rawValue) && rawValue.length === 2)
            {
               rangeArray = [rawValue[0], rawValue[1]];
            }
            else if (rawValue !== undefined && rawValue !== null)
            {
               // If a single number is provided, replicate it
               const num = typeof rawValue === 'number' ? rawValue : (rmConfig.defaultMin !== undefined ? rmConfig.defaultMin : rmConfig.default);
               rangeArray = [num, num];
            }
            else
            {
               // Priority: defaultMin/defaultMax > default array > default single > min/max fallback
               if (rmConfig.defaultMin !== undefined && rmConfig.defaultMax !== undefined)
               {
                  rangeArray = [rmConfig.defaultMin, rmConfig.defaultMax];
               }
               else if (rmConfig.defaultMin !== undefined)
               {
                  rangeArray = [rmConfig.defaultMin, rmConfig.defaultMin];
               }
               else if (rmConfig.defaultMax !== undefined)
               {
                  rangeArray = [rmConfig.defaultMax, rmConfig.defaultMax];
               }
               else if (rmConfig.default !== undefined)
               {
                  if (Array.isArray(rmConfig.default) && rmConfig.default.length === 2)
                  {
                     rangeArray = [rmConfig.default[0], rmConfig.default[1]];
                  }
                  else
                  {
                     const defaultNum = rmConfig.default;
                     rangeArray = [defaultNum, defaultNum];
                  }
               }
               else
               {
                  rangeArray = [rmConfig.min ?? 0, rmConfig.max ?? 100];
               }
            }

            return (
               <RangeInput
                  key={rmKey}
                  value={rangeArray}
                  min={rmConfig.min ?? 0}
                  max={rmConfig.max ?? 100}
                  // step={rmConfig.step ?? 1}
                  onChange={(val) =>
                     setFormData((prev: any) => ({
                        ...prev,
                        [rmKey]: val,
                     }))
                  }
                  disabled={!isEditable(rmConfig)}
               />
            );

         case "geo-json-editor":
            return null
         // Placeholder for future implementation
         // return (
         //       <div key={key} className="p-4 border border-yellow-500 rounded bg-[#1a2234]">
         //             <p className="text-yellow-500 mb-2">
         //                   geo-json-editor widget not yet implemented
         //             </p>
         //             <pre className="text-xs text-gray-400 overflow-auto max-h-60">
         //                   {JSON.stringify(value, null, 2)}
         //             </pre>
         //       </div>
         // );

         default:
            return (
               <div key={key} className="text-red-400">
                  Unsupported widget: {config.widget}
               </div>
            );
      }
   };

   // Helper to determine if a field is required
   const isFieldRequired = (key: string, config: any) =>
   {
      // Check schema required array
      const schemaRequired = schema?.required || [];
      if (schemaRequired.includes(key))
      {
         return true;
      }
      // Check UI config required flag
      if (config.required)
      {
         return true;
      }
      return false;
   };


   const renderRangePair = (key1: string, key2: string, config1: any, config2: any) =>
   {
      const targetMin = key1;
      const targetMax = key2;
      const minVal = formData[targetMin] ?? config1.default ?? config1.min ?? 0;
      const maxVal = formData[targetMax] ?? config2.default ?? config2.max ?? 100;

      // Use the same min/max from either config (they should match)
      const rangeMin = config1.min ?? config2.min ?? 0;
      const rangeMax = config1.max ?? config2.max ?? 100;

      // Determine required status for the pair (use first field's config)
      const isRequired = isFieldRequired(key1, config1);

      // Create base label from config title, clean min/max prefix
      let baseLabel = config1.title || config1.label || key1;
      // Remove "Min " or "Max " prefix
      baseLabel = baseLabel.replace(/^(Min|Max)\s+/i, "");
      // Clean from key if still looks like key
      if (!baseLabel || baseLabel === key1)
      {
         baseLabel = key1.replace(/^(min|max)_/i, "");
         baseLabel = baseLabel.replace(/_/g, " ");
         baseLabel = baseLabel.charAt(0).toUpperCase() + baseLabel.slice(1);
      }

      return (
         <div>
            <div className="text-sm mb-1">
               <span className="text-gray-300">{t(baseLabel)}</span>
               {!isRequired && <span className="text-gray-500 ml-1">(optional)</span>}
            </div>
            <RangeInput
               label={undefined}
               value={[minVal, maxVal]}
               min={rangeMin}
               max={rangeMax}
               onChange={(val) =>
                  setFormData((prev: any) => ({
                     ...prev,
                     [targetMin]: val[0],
                     [targetMax]: val[1],
                  }))
               }

               disabled={!isEditable(config1)}
            />
         </div>
      );
   };

   return (
      // <div className="px-6 pb-6 space-y-4">
      <div className={display === 'grid' ? "px-6 pb-6 grid grid-cols-2 gap-4" : "px-6 pb-6 space-y-4"}>
         {ui.order?.filter((itemF?: any) => itemF !== 'geometry')?.map((key: string, index: number) =>
         {
            const config = ui?.options?.[key];

            if (!config)
            {
               return (
                  <div key={key} className="text-yellow-400">
                     Missing UI configuration for field: {key}
                  </div>
               );
            }

            // Check if field is conditionally hidden
            if (!shouldRenderField(key, config))
            {
               return null;
            }

            // Check if this field should be paired with the next one
            const nextKey = ui.order?.[index + 1];
            const nextConfig = nextKey ? ui?.options?.[nextKey] : null;

            // If this is a range widget and the next one is also a range widget and they form a min/max pair
            if (
               config.widget === "range" &&
               nextConfig?.widget === "range" &&
               nextKey &&
               isMinMaxPair(key, nextKey, config, nextConfig)
            )
            {
               // Render the combined range input and skip the next field
               // RangeInput renders its own label
               return (
                  <div key={`${key}-${nextKey}`}>
                     {renderRangePair(key, nextKey, config, nextConfig)}
                  </div>
               );
            }

            // Skip this field if it's the second part of a pair (already rendered above)
            if (config.widget === "range" && index > 0)
            {
               const prevKey = ui.order?.[index - 1];
               const prevConfig = prevKey ? ui?.options?.[prevKey] : null;
               if (prevConfig?.widget === "range" && isMinMaxPair(prevKey, key, prevConfig, config))
               {
                  return null; // Skip, already rendered with previous
               }
            }

            // Normal single field rendering
            return (
               <div key={key}>
                  <div className="text-sm mb-1">
                     {(() =>
                     {
                        const label = config.title || config.label || key;
                        const isRequired = isFieldRequired(key, config);
                        return (
                           <>
                              <span className="text-gray-300">{t(label)}</span>
                              {!isRequired && <span className="text-gray-500 ml-1">({(t('optional'))})</span>}
                           </>
                        );
                     })()}
                  </div>
                  {renderField(key, config)}
                  {ui?.options?.[key]?.message_alert && (
                     <p className="text-sm text-yellow-400 mt-2 flex items-start gap-1">
                        <CommonIcons.InfoCircleIconAlert />
                        {t(ui?.options?.[key]?.message_alert)}
                     </p>
                  )}
               </div>
            );
         })}
      </div>
   );
};

export default UnifiedDynamicForm;
